import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  Select,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomButton from "../CustomButton/CustomButton";
import CustomNewModal from "../CustomNewModal/CustomNewModal";
import { languageArray } from "../LanguageSelector/LanguageSelectorConstant";
import { changeLanguage } from "../../Store/LanguageReducer/languageAction";
import { setCurrentUserData } from "../../Store/UserProfile/userProfileActions";
import { showToast } from "../../Store/Toast/toastAction";
import { SOMETHING_WENT_WRONG } from "../../consts/errorMessages";
import { getItem, setItem } from "../../Auth/localStorageService";
import {
  setSessionItem,
  removeSessionItem,
} from "../../Auth/sessionStorageService";
import { updateLanguagePreference } from "../../utils/apiService/accountApiService";
import { localeMap, PREFERENCE_OPTIONS } from "../../consts/constant";
import { styles } from "./LoggedInLanguagePopupStyles";

const LoggedInLanguagePopup = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUserData = useSelector(
    (state) => state.userProfile?.currentUserData,
  );

  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [preferenceOption, setPreferenceOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const currentDefaultLangKey = currentUserData?.data?.languagePreferred || "en";
  const currentDefaultLangName =
    languageArray.find((l) => l.key === currentDefaultLangKey)?.label || "English";

  useEffect(() => {
    if (!open) return;
    const saved = getItem("language") || "en";
    setSelectedLanguage(saved);
    setPreferenceOption(null);
  }, [open]);

  const handleApply = async () => {
    const lang = selectedLanguage;
    if (!lang) return;

    if (preferenceOption === PREFERENCE_OPTIONS.SESSION) {
      if (lang !== getItem("language")) {
        dispatch(changeLanguage(lang));
        setItem("language", lang);
        setSessionItem("languageSessionOnly", "true");
      }
      onClose();
    } else if (preferenceOption === PREFERENCE_OPTIONS.DEFAULT) {
      if (localeMap(lang) === currentUserData?.data?.languagePreferred) {
        dispatch(changeLanguage(lang));
        setItem("language", lang);
        removeSessionItem("languageSessionOnly");
        onClose();
        return;
      }
      setIsLoading(true);
      const res = await updateLanguagePreference(lang);
      setIsLoading(false);
      if (res.error) {
        dispatch(
          showToast({
            message: t(SOMETHING_WENT_WRONG),
            type: "error",
          }),
        );
        return;
      }
      dispatch(changeLanguage(lang));
      setItem("language", lang);
      removeSessionItem("languageSessionOnly");
      dispatch(setCurrentUserData({
        ...currentUserData,
        data: {
          ...currentUserData?.data,
          languagePreferred: localeMap(lang),
        },
      }));
      dispatch(
        showToast({
          message: t("LANGUAGE_PREFERENCE_UPDATED"),
          type: "success",
        }),
      );
      onClose();
    }
  };

  const selectedLanguageLocale =
    languageArray.find((l) => l.key === selectedLanguage)?.locale || "en";

  return (
    <CustomNewModal
      openModal={open}
      closeModalHandler={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ p: 3 }}
      includeCloseButton
    >
      <Box sx={styles.container}>
        <Typography sx={styles.heading}>
          {t("SELECT_LANGUAGE_PREFERENCE", { lng: selectedLanguageLocale })}
        </Typography>

        <Select
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
          displayEmpty
          fullWidth
          sx={styles.select}
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography sx={styles.placeholder}>
                  {t("SELECT_LANGUAGE", { lng: selectedLanguageLocale })}
                </Typography>
              );
            }
            const lang = languageArray.find((l) => l.key === value);
            return (
              <Box sx={styles.selectedValue}>
                <Typography
                  component="img"
                  src={lang.flagImage}
                  alt=""
                  sx={styles.flag}
                />
                <Typography sx={styles.selectedLabel}>{lang.label}</Typography>
              </Box>
            );
          }}
        >
          {languageArray.map(({ key, label, flagImage }) => (
            <MenuItem key={key} value={key} sx={styles.menuItem}>
              <Typography
                component="img"
                src={flagImage}
                alt=""
                sx={styles.flag}
              />
              <Typography>{label}</Typography>
            </MenuItem>
          ))}
        </Select>

        <Box sx={styles.optionsContainer}>
          <FormControlLabel
            control={
              <Radio
                checked={preferenceOption === PREFERENCE_OPTIONS.SESSION}
                onChange={() => setPreferenceOption(PREFERENCE_OPTIONS.SESSION)}
                sx={styles.radio}
              />
            }
            label={t("SESSION_ONLY", { lng: selectedLanguageLocale })}
            sx={styles.optionLabel}
          />
          <FormControlLabel
            control={
              <Radio
                checked={preferenceOption === PREFERENCE_OPTIONS.DEFAULT}
                onChange={() => setPreferenceOption(PREFERENCE_OPTIONS.DEFAULT)}
                sx={styles.radio}
              />
            }
            label={t("SET_AS_DEFAULT", { lng: selectedLanguageLocale })}
            sx={styles.optionLabel}
          />
        </Box>

        <Typography sx={styles.currentDefaultNote}>
          {t("CURRENT_DEFAULT_LANGUAGE", { language: currentDefaultLangName, lng: selectedLanguageLocale })}
        </Typography>

        <CustomButton
          isDisabled={!selectedLanguage || !preferenceOption}
          showLoader={isLoading}
          onClick={handleApply}
          classes={styles.applyButton}
        >
          {t("APPLY", { lng: selectedLanguageLocale })}
        </CustomButton>
      </Box>
    </CustomNewModal>
  );
};

LoggedInLanguagePopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default LoggedInLanguagePopup;
